import type { LanguageCode, SheetSettings } from "./types";

export const CURRICULUM_ID = "starter-30" as const;
export const CURRICULUM_VERSION = "2026.07-preview.1";
export const STORAGE_KEY = "sprinkledays:v1";

/**
 * Every canonical URL, sitemap entry, and JSON-LD id is built from this one
 * origin, so a crawler is never offered two addresses for the same page. The
 * site also answers on sprinkledays.pages.dev, which is deliberately not this
 * value: one origin is canonical and the other is the address the deploy
 * happens to produce. Swap this one line to move the whole discovery surface.
 */
export const SITE_URL = "https://sprinkledays.com";
export const SITE_NAME = "SprinkleDays";
/**
 * The pace is a setting between MIN_WORDS and MAX_WORDS, so the copy that
 * cannot follow it does not name a number. The headline in the app names the
 * one the learner actually chose; a search result or a link preview has no
 * learner to read it from, and a fixed number there is a promise the product
 * does not make.
 */
export const SITE_TAGLINE = "Learn a language a few words a day";
export const SITE_DESCRIPTION =
  "Learn a language a few words a day, at a pace you set. One printable sheet with two languages side by side, for learning on your own or testing each other.";

/** Where the reading surface lives. Both the router and the sitemap read it. */
export const ARTICLES_PATH = "/articles";
export const FAQ_PATH = "/faq";

/**
 * How fast an unhurried reader gets through prose, in words a minute. It turns
 * an article body into the reading time shown on the card and the `timeRequired`
 * in its structured data, which have to be the same number.
 */
export const READING_WORDS_PER_MINUTE = 200;

export const LANGUAGE_LABELS: Record<LanguageCode, string> = {
  en: "English",
  nl: "Dutch",
  th: "Thai",
};

export const LANGUAGE_NATIVE_LABELS: Record<LanguageCode, string> = {
  en: "English",
  nl: "Nederlands",
  th: "ไทย",
};

export const SPEECH_LOCALES: Record<LanguageCode, string> = {
  en: "en-US",
  nl: "nl-NL",
  th: "th-TH",
};

export const MIN_WORDS = 3;
export const MAX_WORDS = 12;
export const MIN_SENTENCES = 0;
export const MAX_SENTENCES = 5;

/** The course is handed over a week at a time: seven sheets in one go. */
export const DAYS_PER_WEEK = 7;

/**
 * The first week opens without paying. Nothing in the interface says so: the
 * upgrade notice is what tells the learner, once week two arrives.
 */
export const FREE_WEEKS = 1;
export const FREE_DAYS = FREE_WEEKS * DAYS_PER_WEEK;

/**
 * Where day 8 sends the early list. A Formspree form ID is public by design,
 * so it ships in the client; the endpoint only accepts POSTs and gives nothing
 * back. Swap this one URL to move the list somewhere else.
 */
export const EARLY_ACCESS_ENDPOINT = "https://formspree.io/f/xjybeopd";

/**
 * The journey length depends on the word count, so persisted day numbers are
 * only bounded by the shortest possible pace (the whole pool at MIN_WORDS a
 * day). The real length for the current settings comes from the pool index.
 */
export const MAX_JOURNEY_DAYS = 60;

/**
 * Sentences track the word count so the sheet stays balanced: roughly one
 * sentence for every three words, which lands on the familiar 5 words + 2
 * sentences by default.
 */
export function defaultSentenceCount(words: number): number {
  return clampSentences(Math.max(1, Math.round(words / 3)));
}

export function clampWords(value: number): number {
  if (!Number.isFinite(value)) return 5;
  return Math.min(MAX_WORDS, Math.max(MIN_WORDS, Math.round(value)));
}

export function clampSentences(value: number): number {
  if (!Number.isFinite(value)) return 2;
  return Math.min(MAX_SENTENCES, Math.max(MIN_SENTENCES, Math.round(value)));
}

export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Amsterdam";
  } catch {
    return "Europe/Amsterdam";
  }
}

export function todayInTimezone(timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    calendar: "gregory",
    numberingSystem: "latn",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts();
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

export function createSettings(
  left: LanguageCode,
  right: LanguageCode,
  overrides: Partial<Omit<SheetSettings, "left" | "right">> = {},
): SheetSettings {
  const timezone = overrides.timezone ?? detectTimezone();
  const words = clampWords(overrides.words ?? 5);

  return {
    left,
    right,
    leftLabel: overrides.leftLabel ?? "",
    rightLabel: overrides.rightLabel ?? "",
    words,
    sentences: clampSentences(overrides.sentences ?? defaultSentenceCount(words)),
    showPronunciation: overrides.showPronunciation ?? true,
    timezone,
    startDate: overrides.startDate ?? todayInTimezone(timezone),
  };
}
